import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.previousModalByModalId = new Map()
    this.handleManagedModalHide = this.handleManagedModalHide.bind(this)
    this.registerManagedModalListeners()
  }

  disconnect() {
    this.unregisterManagedModalListeners()
  }

  switch(event) {
    const conditionSelector = event.currentTarget.dataset.modalManagerConditionSelector
    const conditionValue = event.currentTarget.dataset.modalManagerConditionValue
    if (conditionSelector && conditionValue) {
      const node = document.querySelector(conditionSelector)
      if (!node || node.value !== conditionValue) return
    }

    event.preventDefault()

    const fromModalId = event.currentTarget.dataset.modalManagerFromModalId
    const toModalId = event.currentTarget.dataset.modalManagerToModalId
    const formUrl = event.currentTarget.dataset.modalManagerFormUrl
    const subtitle = event.currentTarget.dataset.modalManagerSubtitle
    const rememberPrevious = event.currentTarget.dataset.modalManagerRememberPrevious !== "false"
    if (!toModalId) return

    if (fromModalId && rememberPrevious) {
      this.previousModalByModalId.set(toModalId, fromModalId)
    } else {
      this.previousModalByModalId.delete(toModalId)
    }

    if (!fromModalId) {
      this.showModal(toModalId, { formUrl, subtitle })
      return
    }

    const fromModal = this.modalElement(fromModalId)
    if (fromModal) {
      fromModal.addEventListener("hide", () => this.showModal(toModalId, { formUrl, subtitle }), { once: true })
      this.hideModal(fromModalId)
      return
    }

    this.showModal(toModalId, { formUrl, subtitle })
  }

  registerManagedModalListeners() {
    this.managedModalIds().forEach((modalId) => {
      const modal = this.modalElement(modalId)
      if (!modal) return

      modal.addEventListener("hide", this.handleManagedModalHide)
    })
  }

  unregisterManagedModalListeners() {
    this.managedModalIds().forEach((modalId) => {
      const modal = this.modalElement(modalId)
      if (!modal) return

      modal.removeEventListener("hide", this.handleManagedModalHide)
    })
  }

  handleManagedModalHide(event) {
    const closedModalId = event.currentTarget?.id
    if (!closedModalId) return

    const previousModalId = this.previousModalByModalId.get(closedModalId)
    if (!previousModalId) return

    this.previousModalByModalId.delete(closedModalId)
    this.showModal(previousModalId)
  }

  managedModalIds() {
    return [
      ...new Set(
        Array.from(this.element.querySelectorAll("[data-modal-manager-to-modal-id]"))
          .map((trigger) => trigger.dataset.modalManagerToModalId)
          .filter(Boolean)
      )
    ]
  }

  showModal(modalId, options = {}) {
    if (!modalId) return

    this.configureModal(modalId, options)

    const controller = this.dialogController(modalId)
    if (controller?.dialog) {
      controller.dialog.show()
      return
    }

    document.querySelector(`[data-a11y-dialog-show="${modalId}"]`)?.click()
  }

  hideModal(modalId) {
    if (!modalId) return

    const controller = this.dialogController(modalId)
    if (controller?.dialog) {
      controller.dialog.hide()
      return
    }

    document.querySelector(`[data-a11y-dialog-hide="${modalId}"]`)?.click()
  }

  dialogController(modalId) {
    const modal = this.modalElement(modalId)
    if (!modal) return null

    const getter = this.application?.getControllerForElementAndIdentifier
    if (typeof getter !== "function") return null

    return getter.call(this.application, modal, "a11y-dialog")
  }

  modalElement(modalId) {
    return document.getElementById(modalId)
  }

  configureModal(modalId, options) {
    const modal = this.modalElement(modalId)
    if (!modal) return

    if (options.formUrl) {
      const form = modal.querySelector("form")
      if (form) form.action = options.formUrl
    }

    if (options.subtitle !== undefined) {
      const modalText = modal.querySelector(".modal__text")
      if (!modalText) return

      let subtitle = modalText.querySelector(".modal__subtitle")
      const normalizedSubtitle = String(options.subtitle || "").trim()

      if (normalizedSubtitle.length === 0) {
        if (subtitle) subtitle.remove()
        return
      }

      if (!subtitle) {
        subtitle = document.createElement("p")
        subtitle.className = "modal__subtitle tw-m-0 text-sm"
        modalText.appendChild(subtitle)
      }

      subtitle.textContent = normalizedSubtitle
    }
  }
}
